import React from "react";
import Container from "../ui/Container";
import Card from "../ui/Card";
import { Cpu, HardDrive, DatabaseBackup, Shield, Users, ArrowUpRight } from "lucide-react";

function FeatureItem({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.03]">
        <Icon className="h-5 w-5 text-cyan-300" />
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{title}</div>
        <p className="mt-1 text-sm leading-6 text-white/65">{body}</p>
      </div>
    </div>
  );
}

export default function GameHostingHighlights() {
  return (
    <section className="bg-[#070A0F] py-20">
      <Container>
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-xs font-semibold tracking-wider text-white/50">WHAT YOU GET</div>
          <h2 className="mt-3 text-balance text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl">
            The essentials, done properly<span className="text-cyan-300">.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-white/70 sm:text-lg">
            No add-ons for basics. No mystery tiers. Just clean infrastructure and clear allocation.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
          {/* Left: Hero feature (strong, appealing) */}
          <Card className="p-7 sm:p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-xs font-semibold tracking-wider text-white/50">
                  PERFORMANCE
                </div>
                <h3 className="mt-2 text-2xl font-semibold text-white leading-tight">
                  Predictable performance under load<span className="text-cyan-300">.</span>
                </h3>
                <p className="mt-3 text-sm leading-6 text-white/65">
                  Ryzen-powered nodes with NVMe-first storage and allocation that’s simple to understand.
                  No “up to” promises, no confusing tiers.
                </p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4">
              <FeatureItem
                icon={Cpu}
                title="Modern Ryzen nodes"
                body="High single-core performance where game servers actually benefit."
              />
              <FeatureItem
                icon={HardDrive}
                title="NVMe-first storage"
                body="Fast disk for chunk loading, saves, and modded workloads."
              />
              <FeatureItem
                icon={ArrowUpRight}
                title="Clear allocation"
                body="Resources clearly stated and allocated upfront, no overprovisioning or vague “up to” claims."
              />
            </div>
          </Card>

          {/* Right: Supporting features (clean list) */}
          <div className="space-y-6">
            <Card className="p-7 sm:p-8">
              <div className="text-xs font-semibold tracking-wider text-white/50">RELIABILITY</div>
              <h3 className="mt-2 text-xl font-semibold text-white">
                Backups and protection included<span className="text-cyan-300">.</span>
              </h3>

              <div className="mt-6 space-y-6">
                <FeatureItem
                  icon={DatabaseBackup}
                  title="Automatic backups by default"
                  body="7 daily backups plus manual backups included at no extra cost, so your world is safe when things go wrong."
                />
                <FeatureItem
                  icon={Shield}
                  title="DDoS protection"
                  body="Our standard DDos protection included. We work to keep your server online and mitigate attacks when they happen."
                />
              </div>
            </Card>

            <Card className="p-7 sm:p-8">
              <div className="text-xs font-semibold tracking-wider text-white/50">SUPPORT</div>
              <h3 className="mt-2 text-xl font-semibold text-white">
                Humans, not scripts<span className="text-cyan-300">.</span>
              </h3>

              <div className="mt-6 space-y-6">
                <FeatureItem
                  icon={Users}
                  title="Discord + ticket support"
                  body="Get help with mods, configs, and performance tuning from real people."
                />
              </div>

              <div className="mt-6 space-y-6">
                <FeatureItem
                  icon={Users}
                  title="Community support"
                  body="Our Discord isn’t just for support, it’s a place to connect with other server owners, share tips, and get inspired for your next project."
                />
              </div>

              <p className="mt-5 text-xs leading-5 text-white/55">
                The goal is simple: keep your server fast, stable, and easy to manage.
              </p>
            </Card>
          </div>
        </div>
      </Container>
    </section>
  );
}
